import { useEffect, useRef, useState } from 'react';
import useKeyboardDock from '../hooks/useKeyboardDock';

/**
 * Bottom-docked panel shell — the "panel rises from the bottom" counterpart to
 * the centered `Modal`. Shares the same slot API (headerLeft / headerCenter /
 * headerRight / footer / children / title / showClose) so migrating a modal is a
 * straight swap of the wrapper.
 *
 * Pinned to `bottom: var(--kb-visible, 0px)` (driven by `useKeyboardDock`), with
 * a scrim (tap to dismiss), Escape-to-close, and optional swipe-down dismiss.
 *
 * Props:
 *  - show          : boolean
 *  - onClose       : () => void
 *  - title         : string — header title (used when headerCenter is absent)
 *  - headerLeft    : ReactNode — slot to the right of the close button
 *  - headerCenter  : ReactNode — centered slot (replaces title)
 *  - headerRight   : ReactNode — right-aligned slot
 *  - children      : ReactNode — panel body
 *  - footer        : ReactNode — optional footer
 *  - showClose     : boolean — render the ✕ close button (default true)
 *  - panelClass    : string — extra class on the panel div
 *  - panelStyle    : object — extra inline style
 *  - draggable     : boolean — enable swipe-down dismiss (default false)
 *  - scrim         : boolean — render the dimmed scrim (default true)
 */
export default function BottomPanel({
  show,
  title,
  onClose,
  headerLeft,
  headerCenter,
  headerRight,
  children,
  footer,
  showClose = true,
  panelClass = '',
  panelStyle,
  draggable = false,
  scrim = true,
}) {
  useKeyboardDock({ active: show });

  // ── Swipe-down dismiss (optional; skips the textarea so its scroll wins) ──
  const [dragY, setDragY] = useState(0);
  const touchStartY = useRef(null);

  const handleTouchStart = (e) => {
    if (!draggable) return;
    if (e.target.tagName === 'TEXTAREA') return;
    touchStartY.current = e.touches[0].clientY;
  };
  const handleTouchMove = (e) => {
    if (!draggable || touchStartY.current === null) return;
    const delta = e.touches[0].clientY - touchStartY.current;
    if (delta > 0) setDragY(delta);
  };
  const handleTouchEnd = () => {
    touchStartY.current = null;
    if (draggable && dragY > 120) onClose();
    setDragY(0);
  };

  // ── Escape to close ──
  useEffect(() => {
    if (!show) return;
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [show, onClose]);

  if (!show) return null;

  const dragStyle = draggable && dragY > 0
    ? { transform: `translateY(${dragY}px)`, transition: 'none' }
    : undefined;

  return (
    <>
      {scrim && <div className="bp-scrim" onClick={onClose} aria-hidden="true" />}

      <div
        className={`bottom-panel${panelClass ? ' ' + panelClass : ''}`}
        style={{ ...panelStyle, ...dragStyle }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {(showClose || headerLeft || headerCenter || title) && (
          <div className="bp-toolbar">
            {showClose && (
              <button
                className="bp-btn bp-close"
                onClick={onClose}
                title="Dismiss"
                aria-label="Dismiss"
                type="button"
              >
                &times;
              </button>
            )}
            {headerLeft && <div className="bp-header-left">{headerLeft}</div>}
            {headerCenter ? (
              <div className="bp-header-center">{headerCenter}</div>
            ) : (
              <div className="bp-header-center">{title && <h3 className="bp-title">{title}</h3>}</div>
            )}
            {headerRight && <div className="bp-header-right">{headerRight}</div>}
          </div>
        )}

        <div className="bp-body">{children}</div>

        {footer && <div className="bp-footer">{footer}</div>}
      </div>
    </>
  );
}
